import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { ASSETS } from "@/components/landing/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "address", label: "Address", testId: "field-address" },
  { key: "address_note", label: "Address note", testId: "field-address-note" },
  { key: "hours", label: "Opening hours", testId: "field-hours" },
  { key: "phone", label: "Phone", testId: "field-phone" },
  { key: "facebook_url", label: "Facebook URL", testId: "field-facebook" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    axios.get(`${API}/content`).then((res) => setForm(res.data)).catch(() => setError("Could not load content"));
    axios
      .get(`${API}/admin/reminders`, { withCredentials: true })
      .then((res) => setReminders(res.data.reminders))
      .catch(() => setReminders({}));
  }, []);

  if (user === false || user === null || !form) {
    return (
      <div className="min-h-screen bg-[#1B0F2E] text-[#A08FB8] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice("");
    setError("");
    try {
      const { data } = await axios.put(`${API}/admin/content`, form, { withCredentials: true });
      setForm(data);
      setNotice("Saved. The live site is updated.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-[#1B0F2E] border border-[#3B2760] text-[#F5EFE6] focus:outline-none focus:border-[#FF2E88]";

  return (
    <div className="min-h-screen bg-[#1B0F2E] text-[#F5EFE6] px-6 py-10">
      <div data-testid="admin-dashboard" className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="Amanda's logo" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="display-type text-xl font-bold uppercase">Admin Dashboard</h1>
              <p data-testid="admin-user-email" className="text-xs text-[#A08FB8]">{user.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="text-xs mono-lbl uppercase font-bold text-[#A08FB8] hover:text-[#FF2E88] transition-colors"
          >
            Log Out
          </button>
        </div>

        <form data-testid="content-form" onSubmit={save} className="bg-[#26153F] border border-[#3B2760] rounded-2xl p-6 space-y-4">
          <p className="mono-lbl text-xs uppercase text-[#2DE2E6] font-bold">Site Content</p>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs uppercase mono-lbl text-[#A08FB8] mb-1">{f.label}</label>
              <input
                data-testid={f.testId}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className={inputCls}
              />
            </div>
          ))}
          {error && <p data-testid="content-error" className="text-sm text-[#FF2E88]">{error}</p>}
          {notice && <p data-testid="content-notice" className="text-sm text-[#2DE2E6]">{notice}</p>}

          <div>
            <p className="mono-lbl text-xs uppercase text-[#2DE2E6] font-bold mb-2 mt-2">Event Dates</p>
            <p className="text-xs text-[#A08FB8] mb-3">Set a date &amp; time to activate one-tap calendar saves.</p>
            {(form.events || []).map((ev, i) => (
              <div key={ev.id} className="mb-3">
                <label className="block text-xs uppercase mono-lbl text-[#A08FB8] mb-1">{ev.name}</label>
                <input
                  data-testid={`event-date-input-${ev.id}`}
                  type="datetime-local"
                  value={ev.date ? ev.date.slice(0, 16) : ""}
                  onChange={(e) => {
                    const events = [...form.events];
                    events[i] = { ...ev, date: e.target.value ? new Date(e.target.value).toISOString() : null };
                    setForm({ ...form, events });
                  }}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
            ))}
          </div>

          <div>
            <p className="mono-lbl text-xs uppercase text-[#2DE2E6] font-bold mb-2 mt-2">Menu Items &amp; Prices</p>
            {(form.menu || []).map((item, i) => (
              <div key={item.id} className="flex gap-2 mb-2">
                <input
                  data-testid={`menu-name-${item.id}`}
                  value={item.name}
                  placeholder="Item name"
                  onChange={(e) => {
                    const menu = [...form.menu];
                    menu[i] = { ...item, name: e.target.value };
                    setForm({ ...form, menu });
                  }}
                  className={inputCls}
                />
                <input
                  data-testid={`menu-price-${item.id}`}
                  value={item.price || ""}
                  placeholder="$0.00"
                  onChange={(e) => {
                    const menu = [...form.menu];
                    menu[i] = { ...item, price: e.target.value };
                    setForm({ ...form, menu });
                  }}
                  className={`${inputCls} w-24 shrink-0`}
                />
                <button
                  data-testid={`menu-delete-${item.id}`}
                  type="button"
                  onClick={() => setForm({ ...form, menu: form.menu.filter((_, j) => j !== i) })}
                  className="shrink-0 px-3 rounded-xl border border-[#3B2760] text-[#A08FB8] hover:text-[#FF2E88] hover:border-[#FF2E88] transition-colors"
                  aria-label={`Remove ${item.name}`}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              data-testid="menu-add-button"
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  menu: [...(form.menu || []), { id: `item-${Date.now()}`, name: "", price: "" }],
                })
              }
              className="text-xs mono-lbl uppercase font-bold text-[#2DE2E6] hover:underline"
            >
              + Add Item
            </button>
          </div>

          <button
            data-testid="save-content-button"
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-xl bg-[#FF2E88] text-white font-bold uppercase mono-lbl text-sm hover:bg-[#D61F73] transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </form>

        <div data-testid="reminders-panel" className="bg-[#26153F] border border-[#3B2760] rounded-2xl p-6 mt-6">
          <p className="mono-lbl text-xs uppercase text-[#2DE2E6] font-bold mb-3">Reminder Signups</p>
          {reminders === null ? (
            <p className="text-xs text-[#A08FB8]">Loading…</p>
          ) : Object.keys(reminders).length === 0 ? (
            <p data-testid="reminders-empty" className="text-xs text-[#A08FB8]">
              No signups yet. They&apos;ll appear here when visitors tap Remind Me.
            </p>
          ) : (
            Object.entries(reminders).map(([eventId, emails]) => (
              <div key={eventId} data-testid={`reminders-${eventId}`} className="mb-3">
                <p className="text-sm font-bold capitalize">{eventId.replace(/-/g, " ")} ({emails.length})</p>
                {emails.map((em) => (
                  <p key={em} className="text-xs text-[#A08FB8] ml-2">{em}</p>
                ))}
              </div>
            ))
          )}
        </div>

        <Link to="/" data-testid="view-site-link" className="block text-center text-xs text-[#A08FB8] hover:text-[#F5EFE6] mt-6">
          ← View live site
        </Link>
      </div>
    </div>
  );
}
